import {
	EventIdentifier,
	FeatureTeamEvent,
	type InitFlowComponent,
	TrackingEventType,
	useAnalytics,
	useIntl
} from '@capsicum/common';
import { Button, Goat } from '@capsicum/common-web';
import { showEmailOnAlmostDone } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import styled from 'styled-components';

import { Shell } from '../../ui';

/**
 * If the user has just completed the registration process, show the almost complete screen
 */
export const AlmostCompleteRegistrationCheckerFlow: InitFlowComponent = ({ fallback, next }) => {
	// state
	const [emailOnAlmostDone, setEmailOnAlmostDone] = useAtom(showEmailOnAlmostDone);

	// hooks
	const { $t } = useIntl();
	const analytics = useAnalytics();

	// computed values
	const onClickContinue = useCallback(() => {
		setEmailOnAlmostDone('');
		next();
	}, [next, setEmailOnAlmostDone]);

	// useEffects
	useEffect(() => {
		if (!emailOnAlmostDone) {
			setEmailOnAlmostDone('');

			next();
		}
	}, [emailOnAlmostDone, next, setEmailOnAlmostDone]);

	useEffect(() => {
		if (emailOnAlmostDone) {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
				feature_team: FeatureTeamEvent.OFT,
				page: 'almost_done_page'
			});
		}
	}, [emailOnAlmostDone, analytics]);

	if (!emailOnAlmostDone) return fallback;

	// JSX

	return (
		<S.WrappingView>
			<Shell>
				<Goat image="surprise" testID="almostDone-goat-screen" title={$t('almostDone.title')}>
					<S.ContentContainer>
						<S.Almost>{$t('almostDone.cheering')}</S.Almost>
						<S.Email>{emailOnAlmostDone}</S.Email>
						<S.Info>{$t('almostDone.info')}</S.Info>
						<S.ButtonStyled
							testID="almostDone-continue-button"
							text={$t('almostDone.continue')}
							onClick={onClickContinue}
						/>
					</S.ContentContainer>
				</Goat>
			</Shell>
		</S.WrappingView>
	);
};

AlmostCompleteRegistrationCheckerFlow.keepAlive = true;

namespace S {
	export const WrappingView = styled.div`
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
	`;

	export const ContentContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Almost = styled.label`
		display: block;
		${({ theme }) => theme.fonts.headings.title5};
	`;

	export const Email = styled.label`
		display: block;
		${({ theme }) => theme.fonts.text.text1};
	`;

	export const Info = styled.label`
		display: block;
		${({ theme }) => theme.fonts.text.text2};
	`;

	export const ButtonStyled = styled(Button)`
		${({ theme }) => theme.fonts.text.text2};
		width: 100%;
	`;
}
